import type { BestPerformerItem } from '#leaderboards/components/LBestPerformerSummary.vue'
import { useLeaderboardStore } from '#leaderboards/store/useLeaderboardStore'
import { formatPerformanceComposite } from '#leaderboards/utils/performance'
import { formatValue } from '@forgd/formatter'
import { useQuery } from '@pinia/colada'

export type LeaderboardSummaryPageKey = 'index' | 'depth' | 'volume' | 'spread' | 'kpi'

type AggregatedLeaderboardItem = SuccessResponseBody<'/market-makers-leaderboard/aggregated', 'get'>[number]
type PerformanceLeaderboardItem = SuccessResponseBody<'/market-makers-leaderboard/performance', 'get'>[number]

const SUMMARY_CARD_ORDER: LeaderboardSummaryPageKey[] = ['index', 'depth', 'volume', 'spread', 'kpi']

function findBestByRank(
  items: AggregatedLeaderboardItem[],
  rankFn: (item: AggregatedLeaderboardItem) => number | null | undefined,
) {
  return items.reduce((best, item) => {
    const bestRank = rankFn(best) ?? Infinity
    const itemRank = rankFn(item) ?? Infinity
    return itemRank < bestRank ? item : best
  })
}

function findBestByComposite(items: PerformanceLeaderboardItem[]) {
  return items.reduce((best, item) => {
    const bestComposite = best.composite ?? -Infinity
    const itemComposite = item.composite ?? -Infinity
    return itemComposite > bestComposite ? item : best
  })
}

export function useLeaderboardSummaryCards(currentPage: LeaderboardSummaryPageKey) {
  const store = useLeaderboardStore()

  const { data: aggregated, isLoading: isAggregatedLoading } = useQuery({
    ...buildApiQuerySSRSafe('/market-makers-leaderboard/aggregated', {
      method: 'get',
      params: () => ({
        query: {
          startDate: store.dateRange.startDate,
          endDate: store.dateRange.endDate,
        },
      }),
    }),
    refetchOnWindowFocus: false,
  })

  const { data: performance, isLoading: isPerformanceLoading } = useQuery({
    ...buildApiQuerySSRSafe('/market-makers-leaderboard/performance', {
      method: 'get',
      params: () => ({
        query: {
          startDate: store.dateRange.startDate,
          endDate: store.dateRange.endDate,
        },
      }),
    }),
    refetchOnWindowFocus: false,
  })

  const isLoading = computed(() => isAggregatedLoading.value || isPerformanceLoading.value)

  const allCards = computed<Record<LeaderboardSummaryPageKey, BestPerformerItem | null>>(() => {
    const timeframeSuffix = store.timeframeShortLabel ? ` (${store.timeframeShortLabel})` : ''
    const aggregatedItems = aggregated.value ?? []
    const performanceItems = performance.value ?? []

    const depthCard = aggregatedItems.length
      ? (() => {
          const best = findBestByRank(aggregatedItems, item => item.bidAskDepth.rank)
          return {
            label: `Top Avg. (+/-) 2.00% Depth${timeframeSuffix}`,
            marketMakerName: best.marketMakerName,
            marketMakerImageUrl: best.marketMakerImageUrl,
            value: formatValue(best.bidAskDepth.value, 'currency:0'),
          }
        })()
      : null

    const volumeCard = aggregatedItems.length
      ? (() => {
          const best = findBestByRank(aggregatedItems, item => item.dailyFillVolume.rank)
          return {
            label: `Top ADV: Maker & Taker ${timeframeSuffix}`,
            marketMakerName: best.marketMakerName,
            marketMakerImageUrl: best.marketMakerImageUrl,
            value: formatValue(best.dailyFillVolume.value, 'currency:0'),
          }
        })()
      : null

    const spreadCard = aggregatedItems.length
      ? (() => {
          const best = findBestByRank(aggregatedItems, item => item.topOfBookSpread.rank)
          return {
            label: `Top Bid-Ask Spread${timeframeSuffix}`,
            marketMakerName: best.marketMakerName,
            marketMakerImageUrl: best.marketMakerImageUrl,
            value: formatValue(best.topOfBookSpread.value, 'percent:2'),
          }
        })()
      : null

    const kpiCard = aggregatedItems.length
      ? (() => {
          const best = findBestByRank(aggregatedItems, item => item.combinedUptime.rank)
          return {
            label: 'Top Uptime',
            marketMakerName: best.marketMakerName,
            marketMakerImageUrl: best.marketMakerImageUrl,
            value: formatValue(best.combinedUptime.value, 'percent:2'),
          }
        })()
      : null

    const indexCard = performanceItems.length
      ? (() => {
          const best = findBestByComposite(performanceItems)
          return {
            label: 'Top Performance Index',
            marketMakerName: best.marketMakerName,
            marketMakerImageUrl: best.marketMakerImageUrl,
            value: formatPerformanceComposite(best.composite),
          }
        })()
      : null

    return {
      index: indexCard,
      depth: depthCard,
      volume: volumeCard,
      spread: spreadCard,
      kpi: kpiCard,
    }
  })

  const items = computed(() =>
    SUMMARY_CARD_ORDER
      .filter(pageKey => pageKey !== currentPage)
      .map(pageKey => allCards.value[pageKey])
      .filter((card): card is BestPerformerItem => card !== null),
  )

  return {
    items,
    isLoading,
  }
}
